import { useCallback, useState } from "react";

interface SelectableRow<TRow> {
  id: string;
  subRows: TRow[];
  getIsGrouped: () => boolean;
  getIsSelected: () => boolean;
  toggleSelected: (value?: boolean) => void;
}

interface SelectableRowModel<TRow> {
  rows: TRow[];
  rowsById: Record<string, TRow>;
}

export const flattenRows = <TRow extends SelectableRow<TRow>>(rows: TRow[]) =>
  rows.flatMap((row) => (row.getIsGrouped() ? row.subRows : [row]));

const getRowRange = <TRow extends SelectableRow<TRow>>(
  rows: TRow[],
  selectedRowId: string,
  previousSelectedRowId: string,
) => {
  const range: TRow[] = [];
  const processedRowsMap = {
    [selectedRowId]: false,
    [previousSelectedRowId]: false,
  };
  for (const row of rows) {
    if (row.id === selectedRowId || row.id === previousSelectedRowId) {
      if ("" === previousSelectedRowId) {
        range.push(row);
        break;
      }

      processedRowsMap[row.id] = true;
    }
    if (
      (processedRowsMap[selectedRowId] ||
        processedRowsMap[previousSelectedRowId]) &&
      !row.getIsGrouped()
    ) {
      range.push(row);
    }
    if (
      processedRowsMap[selectedRowId] &&
      processedRowsMap[previousSelectedRowId]
    ) {
      break;
    }
  }

  return range;
};

/**
 * Toggles a row selection, or the range between the previously toggled row
 * and the current one when `multi` is set (shift+click / shift+x).
 */
export function useSelectRows<TRow extends SelectableRow<TRow>>() {
  const [previousSelectedRowId, setPreviousSelectedRowId] =
    useState<string>("");

  return useCallback(
    (rowModel: SelectableRowModel<TRow>, currentRow: TRow, multi: boolean) => {
      if (multi) {
        const { rows, rowsById: rowsMap } = rowModel;
        const flattenedRows = flattenRows(rows);
        const rowsToToggle = getRowRange(
          flattenedRows,
          currentRow.id,
          flattenedRows.map((r) => r.id).includes(previousSelectedRowId)
            ? previousSelectedRowId
            : "",
        );
        const isLastSelected =
          !rowsMap[currentRow.id]?.getIsSelected() || false;
        rowsToToggle.forEach((row) => row.toggleSelected(isLastSelected));
      } else {
        currentRow.toggleSelected();
      }
      setPreviousSelectedRowId(currentRow.id);
    },
    [previousSelectedRowId],
  );
}
